import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MessageCircle, Send, ArrowLeft, User, Bot, AlertTriangle, 
  CheckCircle, Clock, RefreshCw, Search, Car
} from 'lucide-react';
import { LOGO_DARK_BG } from '../config/branding';

const AdminChatsPage = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState('all'); // all, escalated
  const messagesEndRef = useRef(null);
  const adminToken = localStorage.getItem('admin_token');

  useEffect(() => {
    if (!adminToken) {
      navigate('/admin/login');
      return;
    }
    loadSessions();
  }, [adminToken, navigate]);

  useEffect(() => {
    if (selectedSession) {
      loadMessages(selectedSession.id);
    }
  }, [selectedSession]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const endpoint = filter === 'escalated' ? 'escalated' : 'sessions';
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/chat/admin/${endpoint}`,
        { headers: { 'Authorization': `Bearer ${adminToken}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setSessions(data.sessions || []);
      }
    } catch (error) {
      console.error('Load sessions failed:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSessions();
  }, [filter]);

  const loadMessages = async (sessionId) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/chat/admin/session/${sessionId}`,
        { headers: { 'Authorization': `Bearer ${adminToken}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
      }
    } catch (error) {
      console.error('Load messages failed:', error);
    }
  };

  const sendMessage = async () => {
    if (!inputText.trim() || !selectedSession || sending) return;

    const text = inputText.trim();
    setInputText('');
    setSending(true);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/chat/admin/send`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            text,
            session_id: selectedSession.id
          })
        }
      );

      if (res.ok) {
        const data = await res.json();
        setMessages(prev => [...prev, data.message]);
      }
    } catch (error) {
      console.error('Send failed:', error);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) + 
           ' ' + date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  const getSenderIcon = (sender) => {
    if (sender === 'customer') return <User className="w-4 h-4" />;
    if (sender === 'bot') return <Bot className="w-4 h-4" />;
    return <User className="w-4 h-4 text-brand-green" />;
  };

  const getSenderName = (sender) => {
    if (sender === 'customer') return 'Kunde';
    if (sender === 'bot') return 'KFZ-Bot';
    return 'Sie (Admin)';
  };

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar - Sessions List */}
      <div className="w-96 bg-white border-r border-gray-200 flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 bg-slate-900">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <img src={LOGO_DARK_BG} alt="Logo" className="h-8" />
            <h1 className="text-lg font-bold text-white">Kunden-Chats</h1>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setFilter('all')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              filter === 'all' 
                ? 'text-brand-green border-b-2 border-brand-green' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Alle Chats
          </button>
          <button
            onClick={() => setFilter('escalated')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
              filter === 'escalated' 
                ? 'text-amber-600 border-b-2 border-amber-500' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <AlertTriangle className="w-4 h-4" />
            Eskaliert
          </button>
        </div>

        {/* Sessions List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="w-6 h-6 animate-spin text-gray-400 mx-auto" />
            </div>
          ) : sessions.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <MessageCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Keine Chats vorhanden</p>
            </div>
          ) : (
            sessions.map((session) => (
              <button
                key={session.id}
                onClick={() => setSelectedSession(session)}
                className={`w-full p-4 border-b border-gray-100 text-left hover:bg-gray-50 transition-colors ${
                  selectedSession?.id === session.id ? 'bg-green-50' : ''
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    session.escalated ? 'bg-amber-100' : 'bg-gray-100'
                  }`}>
                    {session.escalated ? (
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                    ) : (
                      <Car className="w-5 h-5 text-gray-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-gray-900 truncate">
                        {session.customer_name}
                      </span>
                      {session.unread_by_admin > 0 && (
                        <span className="w-5 h-5 bg-brand-green text-white text-xs rounded-full flex items-center justify-center">
                          {session.unread_by_admin}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 font-mono">{session.license_plate}</p>
                    {session.last_message && (
                      <p className="text-xs text-gray-400 truncate mt-1">
                        {session.last_message.text?.substring(0, 50)}...
                      </p>
                    )}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>

        {/* Refresh Button */}
        <div className="p-3 border-t border-gray-200">
          <button
            onClick={loadSessions}
            className="w-full py-2 text-sm text-gray-600 hover:text-brand-green flex items-center justify-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Aktualisieren
          </button>
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {selectedSession ? (
          <>
            {/* Chat Header */}
            <div className="bg-white border-b border-gray-200 p-4">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  selectedSession.escalated ? 'bg-amber-100' : 'bg-gray-100'
                }`}>
                  <Car className="w-6 h-6 text-gray-600" />
                </div>
                <div>
                  <h2 className="font-bold text-gray-900">{selectedSession.customer_name}</h2>
                  <p className="text-sm text-gray-600">
                    {selectedSession.license_plate} • {selectedSession.customer_email}
                  </p>
                </div>
                {selectedSession.escalated && (
                  <span className="ml-auto px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-medium flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Eskaliert
                  </span>
                )}
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50">
              {messages.map((msg, idx) => (
                <div
                  key={msg.id || idx}
                  className={`flex ${msg.sender === 'admin' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[70%] rounded-2xl px-4 py-3 ${
                      msg.sender === 'admin'
                        ? 'bg-brand-green text-white rounded-br-md'
                        : msg.sender === 'bot'
                        ? 'bg-blue-50 border border-blue-200 text-gray-800 rounded-bl-md'
                        : 'bg-white border border-gray-200 text-gray-800 rounded-bl-md shadow-sm'
                    }`}
                  >
                    <div className={`flex items-center gap-1.5 mb-1 text-xs ${
                      msg.sender === 'admin' ? 'text-green-100' : 'text-gray-500'
                    }`}>
                      {getSenderIcon(msg.sender)}
                      <span className="font-medium">{getSenderName(msg.sender)}</span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                    <p className={`text-[10px] mt-1 ${
                      msg.sender === 'admin' ? 'text-green-100' : 'text-gray-400'
                    }`}>
                      {formatTime(msg.timestamp)}
                    </p>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-gray-200 bg-white">
              <div className="flex gap-3">
                <input
                  type="text"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Antwort eingeben..."
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green"
                  disabled={sending}
                />
                <button
                  onClick={sendMessage}
                  disabled={!inputText.trim() || sending}
                  className="px-6 py-3 bg-brand-green hover:bg-green-600 disabled:bg-gray-300 text-white rounded-xl font-medium flex items-center gap-2 transition-colors"
                >
                  <Send className="w-5 h-5" />
                  Senden
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <MessageCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-600">Wählen Sie einen Chat</h3>
              <p className="text-gray-500">Klicken Sie links auf einen Chat, um die Nachrichten zu sehen</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminChatsPage;
